import React, { useState, useEffect } from "react";
import { FlashcardComponent } from "./FlashcardComponent";
import { makeStyles } from "@material-ui/core/styles";
import { useAuth0 } from "../../react-auth0-spa";
import Alert from "@material-ui/lab/Alert";
import AppLoader from "../AppLoader";
import ImportExportIcon from "@material-ui/icons/ImportExport";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import config from "../../api_config.json";
import Container from "@material-ui/core/Container";
import ErrorApi from "../ErrorApi";
import FeatureNotAvailable from "../FeatureNotAvailable";
import getRandom from "../../utils/getRandom";
import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import { useLocalStorage, writeStorage } from "@rehooks/local-storage";
import Papa from "papaparse";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Fab from "@material-ui/core/Fab";

const NUM_OF_WORDS_PER_GAME = 15;

const Styles = {
  helper: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontSize: "0.9rem",
    color: "#222",
  },
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  bigAvatar: {
    width: 60,
    height: 60,
    margin: "20px auto",
  },
  title: {
    color: "#003087",
  },
  subtitle: {
    color: "#003087",
    margin: theme.spacing(1),
  },
  cardSettings: {
    "text-align": "left",
    "padding-left": theme.spacing(1),
    "padding-right": theme.spacing(1),
  },
  imgFluid: {
    "max-width": "100%",
  },
  fabBtn: {
    marginTop: theme.spacing(2),
    color: "#ffffff",
  },
  submissionMessage: {
    fontSize: "1.2rem",
  },
}));

const googleSheet2Flashcards = (rawData, direction) => {
  return rawData
    .filter((item) => {
      if (
        typeof item["English"] === "string" &&
        typeof item["Español"] === "string" &&
        item["English"] !== "" &&
        item["Español"] !== ""
      ) {
        return true;
      }

      return false;
    })
    .map((item) => {
      if (direction === "en->es") {
        return {
          front: {
            text: item["English"],
          },
          back: {
            text: item["Español"],
          },
        };
      } else {
        return {
          front: {
            text: item["Español"],
          },
          back: {
            text: item["English"],
          },
        };
      }
    });
};

const LS_FLASHCARDS_SIDES = "flashcardInitialSide";

const Flashcards = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [hasApiIssue, setHasApiIssue] = useState(false);
  const { user, isAuthenticated } = useAuth0();
  const [report, setReport] = useState([]);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [data, setData] = useState([]);
  const [initialSide] = useLocalStorage(LS_FLASHCARDS_SIDES, "es->en");
  const { getTokenSilently } = useAuth0();

  const switchCardSides = () => {
    if (initialSide === "es->en") {
      writeStorage(LS_FLASHCARDS_SIDES, "en->es");
    } else {
      writeStorage(LS_FLASHCARDS_SIDES, "es->en");
    }
  };

  const submitReport = async () => {
    const token = await getTokenSilently();

    setSubmissionLoading(true);

    const payload = {
      flashcards: report,
      email: user.email,
    };

    const url = `${config.apiUrl[process.env.REACT_APP_ENV]}/study-report`;

    let responseData;
    let response;

    try {
      response = await fetch(url, {
        method: "post",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      responseData = await response.json();

      if (response.status >= 200 && response.status < 400) {
        setSubmissionSuccess(true);
        setSubmissionError(false);
        setSubmissionLoading(false);
        return responseData;
      }
    } catch (err) {
      setSubmissionSuccess(false);
      setSubmissionError(true);
      console.log({ submissionError });
      setSubmissionLoading(false);
      console.error(err);
    }
  };

  const onStart = () => {
    if (rawData.length <= 0) {
      return;
    }
    setSubmissionLoading(false);
    setSubmissionSuccess(false);
    setSubmissionError(false);
    setReport([]);
    const randomCards = getRandom(rawData, NUM_OF_WORDS_PER_GAME);
    setData(randomCards);
  };

  const onRepeatSet = () => {
    if (rawData.length <= 0) {
      return;
    }
    setSubmissionLoading(false);
    setSubmissionSuccess(false);
    setSubmissionError(false);
    setReport([]);
  };

  const handleReportItem = (data, isCorrect) => {
    const item = {
      ...data,
      correct: isCorrect,
    };

    setReport([...report, item]);
  };

  useEffect(() => {
    setLoading(true);

    const callApi = async () => {
      const token = await getTokenSilently();

      const url = `${config.apiUrl[process.env.REACT_APP_ENV]}/profile`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();
      return responseData.data;
    };

    callApi()
      .then((data) => {
        if (data.flashcardsDeck1) {
          Papa.parse(data.flashcardsDeck1, {
            download: true,
            header: true,
            complete: function (results) {
              const formattedData = googleSheet2Flashcards(
                results.data,
                initialSide
              );

              setRawData(formattedData);

              const randomCards = getRandom(
                formattedData,
                NUM_OF_WORDS_PER_GAME
              );

              setData(randomCards);
              setLoading(false);
            },
          });
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
        setHasApiIssue(true);
      });
  }, [getTokenSilently, initialSide]);

  if (loading) {
    return <AppLoader />;
  }

  if (!isAuthenticated) {
    return <div></div>;
  }

  if (hasApiIssue) {
    console.error(hasApiIssue);

    return <ErrorApi errorMessage="Cannot fetch user profile" />;
  }

  if (loading === false && data.length === 0) {
    return (
      <FeatureNotAvailable message="This feature is not available for your account." />
    );
  }

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h4" className={classes.title}>
            Flashcards
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card className={classes.cardSettings}>
            <CardContent>
              <FlashcardComponent
                dataSource={data}
                onFinish={() => submitReport()}
                onCorrect={(data) => handleReportItem(data, true)}
                onMistake={(data) => handleReportItem(data, false)}
                onStart={onStart}
                onRepeatSet={onRepeatSet}
                reportShared={submissionSuccess || submissionLoading}
                initialSide={initialSide}
              />
              <div style={Styles.helper}>
                <span style={{ marginRight: "20px" }}>
                  <strong>
                    {initialSide === "en->es" && "English to Español"}
                    {initialSide === "es->en" && "Español to English"}
                  </strong>
                </span>
                <Tooltip
                  title="Switch between front and back sides"
                  aria-label="add"
                >
                  <Fab size="small" color="primary" onClick={switchCardSides}>
                    <ImportExportIcon />
                  </Fab>
                </Tooltip>
              </div>
              <div style={Styles.helper}>
                <p>Click the card to see the definition</p>
              </div>
              {submissionLoading && <LinearProgress />}
              <div className={classes.submissionMessage}>
                {submissionSuccess && (
                  <Alert severity="success">
                    Well done! Your progress has been shared with Julieth{" "}
                    <span role="img" aria-label="rocket">
                      🚀
                    </span>
                  </Alert>
                )}
                {submissionError && (
                  <Alert severity="error">
                    An error has occurred during the submission. Please report
                    this error{" "}
                    <span role="img" aria-label="please">
                      🙏
                    </span>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Flashcards;
